import type { Dataset } from "@/api/dataset";
import type { DatasetCameraDetails, DatasetDetails } from "@/api/datasetTrainingOverview";

export const SIGMA_WRISTS = ["wrist_lower", "wrist_upper"] as const;
export const SIGMA_LEGACY_WRISTS = ["wrist_left", "wrist_right"] as const;
type Wrist = (typeof SIGMA_WRISTS)[number];
type CameraShape = [number, number, number];
export const SIGMA_CAMERA_PATH = "policy.mux.observation_image";

type AutomaticWrist = { shape: CameraShape; originalYamlValue: unknown };

// 폼과 함께 갱신해야 StrictMode의 updater 재실행에서도 자동값의 소유권이 보존된다.
export type SigmaDefaultsState = {
  suggestionReady: boolean;
  imageSize: "automatic" | "manual" | "yaml" | null;
  observer: "automatic" | boolean | null;
  wrists: Partial<Record<Wrist, AutomaticWrist | "manual">>;
  removedWrists: Partial<
    Record<(typeof SIGMA_LEGACY_WRISTS)[number], { originalYamlValue: unknown } | "manual">
  >;
};

export type SigmaDatasetDefaults = {
  applicable: boolean;
  cameraNames: string[] | null;
  shapes: Partial<Record<Wrist, CameraShape>>;
  warnings: string[];
};

export function isSigmaDatasetSelection(
  uuids: readonly string[],
  datasets: readonly Pick<Dataset, "uuid" | "embodiment">[],
): boolean {
  const details = new Map(datasets.map((dataset) => [dataset.uuid, dataset]));
  return (
    uuids.length > 0 &&
    uuids.every((uuid) => details.get(uuid)?.embodiment.name === "sigma_hand_1.2")
  );
}

export function sigmaDatasetDefaults(
  uuids: readonly string[],
  datasets: readonly Pick<Dataset, "uuid" | "embodiment">[],
  cameraDetails: readonly Pick<DatasetDetails, "dataset_uuid" | "cameras">[],
): SigmaDatasetDefaults {
  const applicable = isSigmaDatasetSelection(uuids, datasets);
  const result: SigmaDatasetDefaults = { applicable, cameraNames: null, shapes: {}, warnings: [] };
  if (!applicable) return result;
  const byUuid = new Map(cameraDetails.map((details) => [details.dataset_uuid, details]));
  if (!uuids.every((uuid) => byUuid.has(uuid))) return result;
  const cameras = uuids.map((uuid) => byUuid.get(uuid)!.cameras);
  if (cameras.every((items) => items != null)) {
    result.cameraNames = cameras[0]
      .map((camera) => camera.camera)
      .filter((name) => cameras.every((items) => items.some((camera) => camera.camera === name)));
  }
  for (const wrist of SIGMA_WRISTS) {
    const channels = cameras.map((items) => items?.find((camera) => camera.camera === wrist));
    const shapes = channels.map(cameraShape);
    const shape = shapes[0];
    if (
      cameras.some((items) => items != null && !items.some((camera) => camera.camera === wrist))
    ) {
      result.warnings.push(`${wrist}: 일부 Dataset에 카메라가 없어 기존 설정을 유지합니다.`);
    } else if (!shapes.every(isCameraShape)) {
      result.warnings.push(`${wrist}: 해상도를 확정할 수 없어 기존 설정을 유지합니다.`);
    } else if (!shapes.every((value) => JSON.stringify(value) === JSON.stringify(shape))) {
      result.warnings.push(`${wrist}: Dataset마다 해상도가 달라 기존 설정을 유지합니다.`);
    } else if (isCameraShape(shape)) {
      result.shapes[wrist] = shape;
    }
  }
  return result;
}

function cameraShape(camera: DatasetCameraDetails | undefined): CameraShape | null {
  if (camera === undefined || camera.resolutions.length !== 1) return null;
  const resolution = camera.resolutions[0];
  const shape = [3, resolution.height, resolution.width];
  // 측정하지 못한 영상이 남아 있으면 채널 전체의 해상도를 확정하지 않는다.
  return camera.video_count > 0 &&
    resolution.video_count === camera.video_count &&
    isCameraShape(shape)
    ? shape
    : null;
}

function isCameraShape(value: unknown): value is CameraShape {
  return (
    Array.isArray(value) &&
    value.length === 3 &&
    value[0] === 3 &&
    value.every((size) => Number.isInteger(size) && size > 0)
  );
}

export function emptySigmaDefaultsState(): SigmaDefaultsState {
  return { suggestionReady: false, imageSize: null, observer: null, wrists: {}, removedWrists: {} };
}
